import * as Sentry from '~/sentry/sentry_browser_wrapper';
import { buildApiUrl } from '~/api/api_utils';
import axios from '~/lib/utils/axios_utils';
import {
  mapWidgetsFromFeatures as ceMapWidgetsFromFeatures,
  mapWorkItemToGraphQL as ceMapWorkItemToGraphQL,
  parsePageInfo,
} from '~/work_items/list/graphql/rest/work_items_rest_resolver';
import { convertGraphQLVarsToRestParams } from './rest_filter_params_mapper';

const WORK_ITEMS_PATH = '/api/:version/namespaces/:full_path/-/work_items';

const REST_HEALTH_STATUS_TO_GRAPHQL = {
  on_track: 'onTrack',
  needs_attention: 'needsAttention',
  at_risk: 'atRisk',
};

function mapEEWidgetsFromFeatures(features) {
  const eeWidgets = [];

  const startAndDueDateData = features?.start_and_due_date;
  eeWidgets.push({
    __typename: 'WorkItemWidgetStartAndDueDate',
    type: 'START_AND_DUE_DATE',
    dueDate: startAndDueDateData?.due_date ?? null,
    startDate: startAndDueDateData?.start_date ?? null,
  });

  const status = features?.status?.status;
  eeWidgets.push({
    __typename: 'WorkItemWidgetStatus',
    type: 'STATUS',
    status: status
      ? {
          id: status.id,
          name: status.name,
          category: status.category,
          color: status.color,
          description: status.description,
          iconName: status.icon_name,
          position: status.position,
          __typename: 'WorkItemStatus',
        }
      : null,
  });

  const healthStatus = features?.health_status?.health_status;
  eeWidgets.push({
    __typename: 'WorkItemWidgetHealthStatus',
    type: 'HEALTH_STATUS',
    healthStatus: healthStatus ? REST_HEALTH_STATUS_TO_GRAPHQL[healthStatus] ?? healthStatus : null,
  });

  const weight = features?.weight?.weight;
  eeWidgets.push({
    __typename: 'WorkItemWidgetWeight',
    type: 'WEIGHT',
    weight: weight ?? null,
  });

  const iteration = features?.iteration?.iteration;
  eeWidgets.push({
    __typename: 'WorkItemWidgetIteration',
    type: 'ITERATION',
    iteration: iteration
      ? {
          id: iteration.id ? `gid://gitlab/Iteration/${iteration.id}` : null,
          title: iteration.title,
          startDate: iteration.start_date ?? null,
          dueDate: iteration.due_date ?? null,
          webUrl: iteration.web_url ?? null, // eslint-disable-line local-rules/no-web-url
          iterationCadence: iteration.iteration_cadence
            ? {
                id: iteration.iteration_cadence.id
                  ? `gid://gitlab/Iterations::Cadence/${iteration.iteration_cadence.id}`
                  : null,
                title: iteration.iteration_cadence.title,
                __typename: 'IterationCadence',
              }
            : null,
          __typename: 'Iteration',
        }
      : null,
  });

  const linkedItemsData = features?.linked_items;
  eeWidgets.push({
    __typename: 'WorkItemWidgetLinkedItems',
    type: 'LINKED_ITEMS',
    blockingCount: linkedItemsData?.blocking_count ?? 0,
    blockedByCount: linkedItemsData?.blocked_by_count ?? 0,
  });

  return eeWidgets;
}

function mapWidgetsFromFeatures(features, itemNamespace) {
  const ceWidgets = ceMapWidgetsFromFeatures(features, itemNamespace);
  const eeWidgets = mapEEWidgetsFromFeatures(features);

  return [...ceWidgets, ...eeWidgets];
}

function mapWorkItemToGraphQL(item, sharedNamespace) {
  const ceWorkItem = ceMapWorkItemToGraphQL(item, sharedNamespace);
  const itemNamespace = ceWorkItem.namespace;

  return {
    ...ceWorkItem,
    widgets: mapWidgetsFromFeatures(item.features, itemNamespace),
  };
}

export async function workItemsRestResolver(namespace, args) {
  const { fullPath } = namespace;

  const restParams = convertGraphQLVarsToRestParams(args);

  restParams.set(
    'fields',
    'id,iid,global_id,title,title_html,state,created_at,updated_at,closed_at,reference,web_path,author,work_item_type,confidential,hidden,user_discussions_count,namespace',
  );
  restParams.set(
    'features',
    'labels,assignees,milestone,start_and_due_date,status,health_status,weight,iteration,hierarchy,linked_items',
  );

  const url = buildApiUrl(WORK_ITEMS_PATH).replace(':full_path', encodeURIComponent(fullPath));

  let response;
  try {
    response = await axios.get(url, { params: restParams });
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }

  const nodes = (response.data ?? []).map((item) => mapWorkItemToGraphQL(item, namespace));
  const pageInfo = parsePageInfo(response.headers);
  return {
    __typename: 'WorkItemConnection',
    pageInfo,
    nodes,
  };
}
